import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    SelectChangeEvent,
    Autocomplete,
    Checkbox,
    useTheme
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'sonner';
import { TravelType } from 'Application/models';
import { useDestinationStore, useTripStore, useCreatePlanStore } from 'Application/store';
import { ThemeMode } from 'config';

interface AddNewPlan {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

const AddNewPlan: React.FC<AddNewPlan> = ({ isOpen, setIsOpen }) => {
    const [travelType, setTravelType] = useState<TravelType>(TravelType.INDIVIDUAL);
    const [selectedDestinations, setSelectedDestinations] = useState<string[]>([]);
    const { getTrip, tripData } = useTripStore();
    const { getDestinations, destinationData } = useDestinationStore();
    const { createPlan, isLoading } = useCreatePlanStore();

    const [formData, setFormData] = useState({
        planName: '',
        description: '',
        tripName: '',
        allowedDestinations: ''
    });

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    useEffect(() => {
        getDestinations();
        getTrip();
    }, []);

    const handleChange = (e: SelectChangeEvent<TravelType>) => {
        setTravelType(e.target.value as TravelType);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSelectAll = (event: React.ChangeEvent<{}>, value: string[]) => {
        if (value.includes('Select All')) {
            if (selectedDestinations.length === destinationData.length) {
                setSelectedDestinations([]); // Deselect all
            } else {
                setSelectedDestinations(destinationData.map((item) => item.destinationName)); // Select all
            }
        } else {
            setSelectedDestinations(value);
        }
    };

    const handleTripChange = (event: React.ChangeEvent<{}>, newValue: any) => {
        setFormData({
            ...formData,
            tripName: newValue?.name || ''
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const planData = {
            ...formData,
            allowedDestinations: selectedDestinations, // Pass the array directly
            travelType
        };

        try {
            await createPlan(planData);
            toast.success('Plan created successfully');
            setIsOpen(false);
            // Clear the form data
            setFormData({
                planName: '',
                description: '',
                tripName: '',
                allowedDestinations: ''
            });
            setSelectedDestinations([]); // Clear the selected destinations
        } catch (error) {
            toast.error(`Failed to create plan: ${error}`);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Create a new Plan</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Please fill in the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Plan Name
                        </Typography>
                        <TextField
                            placeholder="Enter Plan Name"
                            required
                            name="planName"
                            variant="outlined"
                            value={formData.planName}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Description
                        </Typography>
                        <TextField
                            fullWidth
                            id="outlined-multiline-flexible"
                            multiline
                            rows={3}
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Trip Name
                        </Typography>
                        <Autocomplete
                            id="tripName"
                            options={tripData}
                            getOptionLabel={(option) => option.name}
                            onChange={handleTripChange}
                            renderInput={(params) => <TextField {...params} required name="tripName" fullWidth />}
                        />
                    </div>

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Travel Type
                        </Typography>
                        <Select value={travelType} onChange={handleChange} fullWidth>
                            <MenuItem value={TravelType.INDIVIDUAL}>Individual</MenuItem>
                            <MenuItem value={TravelType.FAMILY}>Family</MenuItem>
                            <MenuItem value={TravelType.STUDENT}>Student</MenuItem>
                            <MenuItem value={TravelType.GROUP}>Group</MenuItem>
                        </Select>
                    </div>

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Allowed Destinations
                        </Typography>
                        <Autocomplete
                            multiple
                            options={['Select All', ...destinationData.map((item) => item.destinationName)]}
                            value={selectedDestinations}
                            onChange={handleSelectAll}
                            disableCloseOnSelect
                            renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                    <Checkbox
                                        checked={
                                            selected || (option === 'Select All' && selectedDestinations.length === destinationData.length)
                                        }
                                        style={{ marginRight: 8 }}
                                    />
                                    {option}
                                </li>
                            )}
                            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select Destinations" />}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isLoading}
                        >
                            Submit
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddNewPlan;
